"use client";

import { useTranslations, useLocale } from "next-intl";
import { Download, FileText, TrendingUp, Award, Check } from "lucide-react";
import { useState, useEffect } from "react";
import Image from "next/image";
import { fetchMemorias } from "@/lib/api/memorias/fetchMemorias";
import { Memorias } from "@/lib/api/memorias/types";
import { type Locale } from "@/i18n/config";

const HERO_IMAGE =
  "https://galeria.tocumenaero.com/gallery/api/Fotos%20Construcci%C3%B3n%20T2/Terminal_existente_1.jpg";

export default function MemoriasPage() {
  const t = useTranslations("memoriasPage");
  const locale = useLocale();
  const [memorias, setMemorias] = useState<Memorias[]>([]);

  useEffect(() => {
    async function loadMemorias() {
      try {
        const data = await fetchMemorias(locale as Locale);
        setMemorias(data);
      } catch (error) {
        console.error("Error fetching memorias:", error);
      }
    }
    loadMemorias();
  }, [locale]);

  const handleOpenPDF = (pdfUrl: string) => {
    window.open(pdfUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <main className="min-h-screen bg-white">
      {/* Hero con imagen */}
      <section className="relative h-[55vh] min-h-[420px] flex items-end overflow-hidden">
        <Image
          src={HERO_IMAGE}
          alt={t("title")}
          fill
          className="object-cover"
          priority
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" />
        <div className="relative z-10 max-w-[80%] mx-auto w-full pb-16 md:pb-20">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white font-semibold mb-4">
              <span className="w-8 h-px bg-white" />
              {t("eyebrow")}
            </span>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 tracking-tight text-balance">
              {t("title")}
            </h1>
            <p className="text-white/80 text-base md:text-lg leading-relaxed">
              {t("subtitle")}
            </p>
          </div>
        </div>
      </section>

      {/* Informes */}
      <section className="max-w-[80%] mx-auto py-24">
        <div className="max-w-3xl mb-14">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#102542] font-semibold mb-4">
            <span className="w-8 h-px bg-[#102542]" />
            {t("allReportsEyebrow")}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 tracking-tight">
            {t("allReports")}
          </h2>
          <p className="text-gray-600 text-base leading-relaxed">
            {t("allReportsSubtitle")}
          </p>
        </div>

        {memorias.length === 0 ? (
          <div className="py-16 text-center text-gray-500 text-base">
            {t("noReports")}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
            {memorias.map((memoria, index) => (
              <article
                key={memoria.id}
                className="bg-white p-8 md:p-10 flex flex-col transition-colors hover:bg-gray-50"
              >
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <span className="text-xs uppercase tracking-wider text-gray-400 font-semibold">
                      {t("year")}
                    </span>
                    <p className="text-5xl font-bold text-[#102542] tracking-tight tabular-nums leading-none mt-1">
                      {memoria.anio}
                    </p>
                  </div>
                  {index === 0 && (
                    <span className="inline-flex items-center text-xs uppercase tracking-wider font-semibold text-[#102542] border border-[#102542] rounded-full px-3 py-1">
                      {t("latestReport")}
                    </span>
                  )}
                </div>

                <h3 className="text-lg font-semibold text-gray-900 mb-3 tracking-tight">
                  {memoria.titulo}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-6">
                  {memoria.descripcion}
                </p>

                {memoria.highlights?.length > 0 && (
                  <ul className="space-y-2 mb-8">
                    {memoria.highlights.map((highlight, idx) => (
                      <li
                        key={idx}
                        className="flex items-start gap-3 text-sm text-gray-700 leading-relaxed"
                      >
                        <Check className="w-4 h-4 text-[#102542] mt-0.5 flex-shrink-0" />
                        <span>{highlight}</span>
                      </li>
                    ))}
                  </ul>
                )}

                <button
                  onClick={() => handleOpenPDF(memoria.documento)}
                  className="mt-auto inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#102542] text-white text-xs uppercase tracking-wider font-semibold hover:bg-[#0a1a30] transition-colors"
                >
                  <Download className="w-4 h-4" />
                  {t("downloadPDF")}
                </button>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* Stats */}
      {memorias.length > 0 && (
        <section className="max-w-[80%] mx-auto pb-24">
          <div className="max-w-3xl mb-14">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#102542] font-semibold mb-4">
              <span className="w-8 h-px bg-[#102542]" />
              {t("statsEyebrow")}
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
              {t("statsTitle")}
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
            <div className="bg-white p-8 flex flex-col justify-between min-h-[160px]">
              <FileText className="w-6 h-6 text-[#102542]" />
              <div>
                <p className="text-3xl md:text-4xl font-bold text-[#102542] tracking-tight tabular-nums">
                  {memorias.length}
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  {t("statsReports")}
                </p>
              </div>
            </div>
            <div className="bg-white p-8 flex flex-col justify-between min-h-[160px]">
              <TrendingUp className="w-6 h-6 text-[#102542]" />
              <div>
                <p className="text-3xl md:text-4xl font-bold text-[#102542] tracking-tight tabular-nums">
                  100%
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  {t("statsTransparency")}
                </p>
              </div>
            </div>
            <div className="bg-white p-8 flex flex-col justify-between min-h-[160px]">
              <Award className="w-6 h-6 text-[#102542]" />
              <div>
                <p className="text-3xl md:text-4xl font-bold text-[#102542] tracking-tight tabular-nums">
                  {memorias[0]?.anio}
                </p>
                <p className="text-sm text-gray-500 mt-2">{t("statsLatest")}</p>
              </div>
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
